import React, { useMemo, useState } from "react";
import { fmtDate } from "@/lib/constants";
import { PaperPlaneRight, ArrowFatLinesRight, CopySimple, ListChecks } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import { CheckBadge, DeliveryReceipt } from "@/components/leads/DeliveryReceipt";
import { DeliveryWhy } from "@/components/leads/DeliveryWhy";

/* -----------------------------------------------------------------------
 * Delivery attempts table + Iter-54.2 Auditability UI.
 * Above the table we render a per-destination roll-up so ops can answer
 * "Where did this lead land, and what happened at each destination?"
 * without eyeballing a chronological attempts log.
 * ---------------------------------------------------------------------- */

/** Latest-attempt status for each destination_id in `attempts`. */
function rollupByDestination(attempts) {
  const byId = new Map();
  const sorted = [...(attempts || [])].sort((a, b) => {
    // Latest first
    if (a.created_at === b.created_at) return (b.attempt_no || 0) - (a.attempt_no || 0);
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
  for (const a of sorted) {
    if (!a.destination_id) continue;
    if (byId.has(a.destination_id)) continue;
    byId.set(a.destination_id, a);
  }
  return byId;
}

/** Pretty label for a delivery-attempt status. */
function outcomeLabel(status) {
  switch (status) {
    case "delivered": return { label: "success", tone: "bg-emerald-600 text-white" };
    case "retrying": return { label: "retrying", tone: "bg-amber-500 text-black" };
    case "failed_final": return { label: "failed", tone: "bg-red-600 text-white" };
    case "abandoned": return { label: "abandoned", tone: "bg-zinc-600 text-white" };
    case "superseded": return { label: "superseded", tone: "bg-zinc-400 text-black" };
    default: return { label: status || "pending", tone: "bg-zinc-200 text-black" };
  }
}

function DestinationRow({ role, id, name, roll }) {
  const outcome = roll ? outcomeLabel(roll.status) : { label: "not attempted", tone: "bg-zinc-100 text-zinc-600" };
  return (
    <div
      className="flex items-center justify-between border border-black px-3 py-2 bg-white"
      data-testid={`delivery-audit-row-${role}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-[9px] uppercase tracking-widest font-mono bg-black text-white px-2 py-0.5 shrink-0">
          {role === "primary" ? "Primary" : "CC"}
        </span>
        <div className="min-w-0">
          <div className="font-chivo font-bold text-sm truncate" data-testid={`delivery-audit-name-${role}`}>
            {name || <span className="italic text-zinc-500">Unknown destination</span>}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">{id}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0 pl-3">
        {roll && (
          <span className="text-[10px] font-mono text-zinc-600" data-testid={`delivery-audit-attempt-${role}`}>
            #{roll.attempt_no}
          </span>
        )}
        <span
          className={`text-[10px] uppercase tracking-widest px-2 py-1 border border-black ${outcome.tone}`}
          data-testid={`delivery-audit-status-${role}`}
        >
          {outcome.label}
        </span>
      </div>
    </div>
  );
}

/** Delivery attempts table + Multi-destination audit summary (iter-54.2). */
function AttemptRow({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-zinc-200" data-testid={`delivery-attempt-${a.id}`}>
        <td className="py-2 text-[11px]">{fmtDate(a.created_at)}</td>
        <td className="py-2">
          {a.destination_name}
          {a.is_copy && <span className="ml-1 border border-black px-1 text-[9px] uppercase tracking-widest" data-testid={`delivery-attempt-extra-${a.id}`}>Extra place</span>}
        </td>
        <td className="py-2">{a.destination_type}</td>
        <td className="py-2">#{a.attempt_no}</td>
        <td className="py-2">
          <StatusBadge status={a.status} />
          {a.rejected_reason && (
            <span className="ml-1 border border-red-700 bg-red-600 px-1 text-[9px] uppercase tracking-widest text-white" title={a.rejected_reason}
                  data-testid={`delivery-attempt-rejected-${a.id}`}>Rejected by buyer</span>
          )}
        </td>
        <td className="py-2">{a.response_code}</td>
        <td className="py-2 text-[11px]">{a.next_retry_at ? fmtDate(a.next_retry_at) : "—"}</td>
        <td className="py-2 max-w-[240px] truncate text-[11px]" title={a.rejected_reason || a.response_body || ""}>
          {a.rejected_reason ? `Buyer said: ${a.rejected_reason}` : a.response_body || "—"}
        </td>
        <td className="py-2"><CheckBadge check={a.field_check} id={a.id} /></td>
        <td className="py-2">
          {a.sent_payload && (
            <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`delivery-receipt-toggle-${a.id}`}
                    className="whitespace-nowrap border border-black px-2 py-0.5 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white">
              {open ? "Hide" : "What they got"}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-black"><td colSpan={10} className="bg-zinc-50 p-3"><DeliveryReceipt attempt={a} /></td></tr>
      )}
    </>
  );
}

function BuyerStatuses({ statuses }) {
  const rows = Object.entries(statuses || {});
  if (!rows.length) return null;
  return (
    <div className="brut-card mt-4 p-5" data-testid="lead-buyer-statuses">
      <div className="font-chivo font-bold uppercase tracking-tight mb-3 flex items-center gap-2">
        <ListChecks size={16} weight="bold" /> Buyer status
      </div>
      {rows.map(([org, s]) => (
        <div key={org} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-200 py-2 text-sm last:border-b-0" data-testid={`lead-buyer-status-${org}`}>
          <b>{s.org_name || "Buyer"}</b>
          <span className="border border-black bg-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-white">{s.label}</span>
          {s.raw && s.raw !== s.label && <span className="text-[11px] text-zinc-600">their word: &ldquo;{s.raw}&rdquo;</span>}
          <span className="font-mono text-[10px] text-zinc-500">{s.source === "api" ? "from their system" : `in their portal by ${s.by}`} · {fmtDate(s.at)}</span>
          {s.notes && <span className="w-full text-[11px] text-zinc-700">{s.notes}</span>}
        </div>
      ))}
    </div>
  );
}

export default function LeadDelivery({ attempts, lead, onChanged }) {
  const extra = (lead && lead.extra) || {};
  const primaryId = extra.forced_destination_id || null;
  const ccIds = useMemo(
    () =>
      Array.isArray(extra.forced_cc_destination_ids)
        ? extra.forced_cc_destination_ids.filter((x) => x && x !== primaryId)
        : [],
    [extra.forced_cc_destination_ids, primaryId]
  );

  const roll = useMemo(() => rollupByDestination(attempts), [attempts]);

  // Resolve human names — prefer roll-up (has destination_name), fallback to raw id.
  const nameFor = (id) => {
    const r = id ? roll.get(id) : null;
    if (r && r.destination_name) return r.destination_name;
    // If the primary is the ONLY attempt, use its name as a hint.
    const any = (attempts || []).find((a) => a.destination_id === id);
    return any ? any.destination_name : "";
  };

  // Compose the audit-summary payload for the copy button.
  const auditText = useMemo(() => {
    const lines = [];
    if (primaryId) {
      const r = roll.get(primaryId);
      lines.push(
        `Primary destination: ${nameFor(primaryId) || primaryId} — ${
          r ? outcomeLabel(r.status).label : "not attempted"
        }`
      );
    }
    ccIds.forEach((id, i) => {
      const r = roll.get(id);
      lines.push(
        `CC destination ${i + 1}: ${nameFor(id) || id} — ${
          r ? outcomeLabel(r.status).label : "not attempted"
        }`
      );
    });
    return lines.join("\n");
  }, [primaryId, ccIds, roll, attempts]);

  const showAudit = Boolean(primaryId) || ccIds.length > 0;

  return (
    <>
      {lead?.id && <DeliveryWhy leadId={lead.id} onChanged={onChanged} />}
      <BuyerStatuses statuses={lead?.buyer_statuses} />
      {showAudit && (
        <div className="brut-card mt-4 p-5" data-testid="delivery-audit">
          <div className="font-chivo font-bold uppercase tracking-tight mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <ArrowFatLinesRight size={16} weight="bold" /> Multi-destination routing (audit)
            </span>
            {auditText && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(auditText).catch(() => {});
                }}
                data-testid="delivery-audit-copy"
                className="text-[10px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors duration-100 inline-flex items-center gap-1"
              >
                <CopySimple size={12} /> Copy
              </button>
            )}
          </div>
          <div className="text-[11px] label-mono mb-3">
            {extra.custom_landing_firm_name
              ? `Routed from custom landing page — ${extra.custom_landing_firm_name}.`
              : "Routed via forced destination bundle."}{" "}
            One consumer lead is billed once regardless of how many destinations receive it.
          </div>
          <div className="grid grid-cols-1 gap-2">
            {primaryId && (
              <DestinationRow
                role="primary"
                id={primaryId}
                name={nameFor(primaryId)}
                roll={roll.get(primaryId)}
              />
            )}
            {ccIds.map((id, i) => (
              <DestinationRow
                key={id}
                role={`cc-${i}`}
                id={id}
                name={nameFor(id)}
                roll={roll.get(id)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="brut-card mt-4 p-5" data-testid="delivery-attempts">
        <div className="font-chivo font-bold uppercase tracking-tight mb-3 flex items-center gap-2">
          <PaperPlaneRight size={16} weight="bold" /> Delivery attempts
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 text-left">When</th>
              <th className="py-2 text-left">Destination</th>
              <th className="py-2 text-left">Type</th>
              <th className="py-2 text-left">Attempt</th>
              <th className="py-2 text-left">Status</th>
              <th className="py-2 text-left">Code</th>
              <th className="py-2 text-left">Next retry</th>
              <th className="py-2 text-left">Response</th>
              <th className="py-2 text-left">Fields</th>
              <th className="py-2 text-left" aria-label="What they received" />
            </tr>
          </thead>
          <tbody>
            {attempts.map((a) => <AttemptRow key={a.id} a={a} />)}
            {!attempts.length && (
              <tr>
                <td colSpan={10} className="py-6 text-center label-mono">
                  No delivery attempts yet. &quot;Where this lead went&quot; above says why.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}
